<template>
    <div>
        <div class="mt-4 block relative faded border px-3 pt-3 pb-2 rounded-t-lg bg-white border-gray-300">
            <div class="font-semibold">
                Payment Timeline
            </div>
        </div>
        <div class="block relative faded border-l border-r border-b p-3 rounded-b-lg bg-white border-gray-300">
            <div v-for="(payment, i) in payments" class="flex">
                <div class="flex-initial pr-2 pt-0.5 flex flex-col items-center" :class="{
                    'font-semibold': isDue(payment),
                    [`text-green-500`]: payment.status !== 'declined',
                    'text-red-500': payment.status === 'declined'
                 }">
                    <svg v-if="payment.status === 'declined'" class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                    </svg>
                    <svg v-else-if="! isDue(payment)" class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd" />
                    </svg>
                    <svg v-else class="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <div v-if="(i+1) !== payments.length" class="flex-1 w-1 bg-gray-100" />
                </div>
                <div class="flex-1 text-sm pr-2 pb-4" :class="{
                    'font-semibold': isDue(payment),
                        'text-gray-500': payment.status !== 'declined',
                        'text-red-500': payment.status === 'declined'
                    }">

                    {{ paymentVerb(payment) }}: {{ payment.payment_at | moment('MMM Do, YYYY') }}<br />

                    <div class="font-normal pt-0.5">
                        <svg v-if="payment.type === 'card'" class="inline w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                        </svg>
                        <svg v-else class="inline w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                        </svg>
                        *{{ payment.billing_profile ? payment.type === 'card' ? payment.billing_profile.cc_last_four : payment.billing_profile.account_last_4 : '' }}
                        <template v-if="! account.needs_new_payment_plan && payment.status === 'declined'">
                            <a @click.prevent="reschedule" class="font-semibold text-gray-700 ml-4 underline cursor-pointer">Re-try / Reschedule</a>
                        </template>
                        <template v-else>
                            <template v-if="(
                                (i+1) === payments.length
                                && pendingPayments.length === 1
                            ) || (
                                pendingPayments.length
                                && payment.id === pendingPayments[0].id
                                && withinTwoWeeks(payment)
                            ) && ! isToday(payment)
                            && ! hasProcessedPaymentsToday">
                                <a @click.prevent="payEarly(payment)" class="inline-block font-semibold text-gray-700 ml-4 underline cursor-pointer">Make Payment Early</a>
                                <div>
                                    <a @click.prevent="$root.$refs.app.showAddToCalendarModalForPayment(payment)" class="inline-block font-semibold text-gray-700 mt-1.5 underline cursor-pointer">Add to Calendar</a>
                                </div>
                            </template>
                            <a v-if="canRescheduleNextPendingPayment
                                && (
                                    pendingPayments.length
                                    && payment.id === pendingPayments[0].id
                                    && payment.id !== payments[0].id
                                ) && ! isToday(payment)"
                               @click.prevent="reschedule(payment)"
                               class="inline-block font-semibold text-gray-700 underline cursor-pointer mt-1.5">Reschedule</a>
                        </template>
                    </div>

                </div>
                <div class="flex-initial flex flex-col justify-between sm:flex-row text-sm text-right pb-4" :class="{
                    'font-semibold': isDue(payment),
                    'text-gray-500': payment.status !== 'declined',
                    'text-red-500': payment.status === 'declined'
                }">
                    <span>{{ ! isDue(payment) ? `-` : `` }}{{ payment.amount | money }}</span>
                    <span class="sm:-order-1 sm:mr-4">
                        <a v-if="payment.status === 'posted'"
                            href="#send-receipt"
                            @click.prevent="sendReceiptFor = payment"
                            :class="{
                                'py-1 px-1.5 text-white text-xs font-medium rounded': true,
                                'bg-gray-400 hover:bg-gray-500 focus:ring-gray-500': ! payment.hasOwnProperty('receipt_sent') || ! payment.receipt_sent,
                                'bg-green-600 hover:bg-green-500 focus:ring-green-500': payment.hasOwnProperty('receipt_sent') && payment.receipt_sent,
                            }">
                            <template v-if="! payment.hasOwnProperty('receipt_sent') || ! payment.receipt_sent">
                                Email Receipt
                            </template>
                            <template v-else-if="payment.hasOwnProperty('receipt_sent') && payment.receipt_sent">
                                Receipt Sent
                            </template>
                        </a>
                    </span>
                </div>
            </div>
        </div>
        <send-receipt-modal
            v-if="!! sendReceiptFor"
            :show="true"
            :account="account"
            :payment="sendReceiptFor"
            @sent="payment => payment.receipt_sent = true"
            @close="sendReceiptFor = null"
        />
    </div>
</template>

<script>
    import Moment from 'moment';
    import SendReceiptModal from "../../components/Modals/SendReceiptModal.vue";

    export default {
        components: {SendReceiptModal},

        props: ['account', 'payments', 'billing-profile', 'has-processed-payments-today', 'can-reschedule-next-pending-payment'],

        computed: {
            /**
             * @return {Array}
             */
            pendingPayments() {
                return this.payments.filter(p => p.status === 'pending');
            }
        },

        data() {
            return {
                sendReceiptFor: null,
            }
        },

        methods: {
            /**
             * @param {Object} payment
             * @returns {Boolean}
             */
            isDue(payment) {
                return payment.status !== 'posted';
            },

            /**
             * @param payment
             * @returns {String}
             */
            paymentVerb(payment) {
                switch (payment.status) {
                    case 'posted':
                        return 'Paid';
                    case 'processed':
                        return 'Processing';
                    case 'declined':
                        return 'Declined';
                    default:
                        return 'Due';
                }
            },

            /**
             * @returns {void}
             */
            reschedule(payment) {
                if (payment.hasOwnProperty('id') === false) {
                    this.$root.$emit('show-payment-reschedule', {
                        payments: this.payments.filter(p => ['declined'].includes(p.status)),
                        pendingPayments: this.payments.filter(p => ['pending'].includes(p.status)),
                        account: this.account,
                        billingProfile: this.billingProfile
                    });
                } else {
                    this.$root.$emit('show-payment-reschedule', {
                        payments: [payment],
                        pendingPayments: this.payments.filter(p => ['pending'].includes(p.status)),
                        account: this.account,
                        billingProfile: this.billingProfile
                    });
                }
            },

            /**
             * @param payment
             * @returns {void}
             */
            payEarly(payment) {
                this.$root.$emit('show-early-payment', {
                    payment,
                    account: this.account,
                    billingProfile: this.billingProfile
                });
            },

            isToday(payment) {
                return new Moment(payment.payment_at).isSame(new Date(), 'day');
            },

            withinTwoWeeks(payment) {
                return ! new Moment(payment.payment_at).isAfter(
                    new Moment().add('14', 'd').endOf('day')
                );
            }
        }
    }
</script>
